import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-35">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-36">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/70" onClick={onClose} data-id="element-37" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3
      }} className="relative bg-cream text-navy rounded-sm shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden z-10" data-id="element-38">
            <div className="flex items-center justify-between p-6 border-b border-navy/10" data-id="element-39">
              <h2 className="text-2xl font-serif font-bold" data-id="element-40">{title}</h2>
              <button onClick={onClose} className="p-2 hover:bg-navy/5 rounded-full transition-colors" data-id="element-41">
                <X size={20} data-id="element-42" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] font-sans text-navy/80 text-sm leading-relaxed space-y-4" data-id="element-43">
              {children}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-navy-dark text-cream/40 py-12 border-t border-white/5" data-id="element-44">
        <div className="max-w-6xl mx-auto px-6 text-center font-sans text-sm" data-id="element-45">
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-46">
            <button onClick={() => setActiveModal('privacy')} className="text-cream/50 hover:text-gold transition-colors underline underline-offset-4" data-id="element-47">
              Política de Privacidad
            </button>
            <span className="text-cream/20" data-id="element-48">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream/50 hover:text-gold transition-colors underline underline-offset-4" data-id="element-49">
              Términos y Condiciones
            </button>
          </div>
          <p className="mb-4" data-id="element-50">
            © {new Date().getFullYear()} Protocolo de Alerta. Todos los
            derechos reservados.
          </p>
          <p className="text-xs max-w-2xl mx-auto opacity-60" data-id="element-51">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-52">
        <p data-id="element-53">
          <strong data-id="element-54">Última actualización:</strong> Mayo 2026
        </p>
        <p data-id="element-55">
          En Protocolo de Alerta, nos comprometemos a proteger tu privacidad.
          Esta política describe cómo recopilamos, usamos y protegemos tu
          información personal.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-56">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-57">
          Recopilamos únicamente la información necesaria para procesar tu
          compra y entregarte el producto digital, incluyendo: nombre, dirección
          de correo electrónico y datos de pago (procesados de forma segura por
          nuestro proveedor de pagos).
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-58">
          2. Uso de la Información
        </h3>
        <p data-id="element-59">Tu información se utiliza exclusivamente para:</p>
        <ul className="list-disc pl-6 space-y-1" data-id="element-60">
          <li data-id="element-61">Procesar y entregar tu compra.</li>
          <li data-id="element-62">
            Enviarte actualizaciones relacionadas con el producto adquirido.
          </li>
          <li data-id="element-63">Responder a consultas de soporte.</li>
        </ul>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-64">
          3. Protección de Datos
        </h3>
        <p data-id="element-65">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado, pérdida
          o alteración.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-66">
          4. Compartir Información
        </h3>
        <p data-id="element-67">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros, excepto cuando sea necesario para procesar tu pago o cuando
          la ley lo requiera.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-68">
          5. Tus Derechos
        </h3>
        <p data-id="element-69">
          Tienes derecho a acceder, rectificar o eliminar tu información
          personal en cualquier momento. También puedes solicitar la cancelación
          de comunicaciones comerciales.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-70">
          6. Cookies
        </h3>
        <p data-id="element-71">
          Este sitio puede utilizar cookies para mejorar tu experiencia de
          navegación y analizar el tráfico del sitio. Puedes configurar tu
          navegador para rechazar cookies.
        </p>
      </Modal>

      {/* Terms & Conditions Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-72">
        <p data-id="element-73">
          <strong data-id="element-74">Última actualización:</strong> Mayo 2026
        </p>
        <p data-id="element-75">
          Al acceder y utilizar este sitio web y adquirir el Protocolo de
          Alerta, aceptas los siguientes términos y condiciones.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-76">
          1. Producto Digital
        </h3>
        <p data-id="element-77">
          El Protocolo de Alerta es un producto digital de carácter informativo
          y educativo. No sustituye el consejo médico profesional. Consulta
          siempre con tu pediatra antes de implementar cambios en la rutina de
          sueño de tu bebé.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-78">
          2. Entrega
        </h3>
        <p data-id="element-79">
          Tras completar la compra, recibirás acceso inmediato al contenido
          digital. El acceso se entregará a la dirección de correo electrónico
          proporcionada durante la compra.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-80">
          3. Propiedad Intelectual
        </h3>
        <p data-id="element-81">
          Todo el contenido del Protocolo de Alerta está protegido por derechos
          de autor. Queda prohibida su reproducción, distribución o reventa sin
          autorización expresa.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-82">
          4. Política de Reembolso
        </h3>
        <p data-id="element-83">
          Debido a la naturaleza digital del producto, no se ofrecen reembolsos
          una vez que se ha accedido al contenido. Si experimentas problemas
          técnicos, nuestro equipo te asistirá para resolverlos.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-84">
          5. Limitación de Responsabilidad
        </h3>
        <p data-id="element-85">
          Los resultados pueden variar según cada familia y situación
          particular. No garantizamos resultados específicos. El contenido se
          proporciona "tal cual" con fines educativos.
        </p>
        <h3 className="font-serif text-lg font-bold text-navy mt-6" data-id="element-86">
          6. Modificaciones
        </h3>
        <p data-id="element-87">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones entrarán en vigor desde su publicación en
          este sitio.
        </p>
      </Modal>
    </>;
}